import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { useTranslation } from "react-i18next";
import Icons from "../../../assets/Icons";
import CopilotBg from "../../../assets/Icons/CopilotBg.png";
import useCopilotValue, {
  appendCopilotAssistantMessage,
  appendCopilotBroadcastMessage,
  appendCopilotUserMessage,
  clearCopilotDraft,
  getCopilotDraft,
  getCopilotValue,
  setCopilotDraft,
  setPendingSuggestions,
  startNewCopilotSession,
  updateCopilotBroadcastStatus,
  updateCopilotRecommendationStatus,
} from "../hooks/useCopilotValue";
import useCopilotChatMutation from "../hooks/useCopilotChatMutation";
import { COPILOT_MESSAGE, COPILOT_RECIPIENT, COPILOT_SUGGESTION } from "../types/CommonTypes";
import axios from "axios";
import MemoizedMarkdown from "./MemoizedMarkdown";
import RecommendCard from "./RecommendCard";
import BroadcastCard from "./BroadcastCard";
import useGetBroadcastRecipientsQuery, { ELIGIBLE_USER_FOR_SMS } from "../hooks/useGetBroadcastRecipientsQuery";
import useSendBroadcastSmsMutation from "../hooks/useSendBroadcastSmsMutation";

function resolvePhoneNumbers(
  recipients: COPILOT_RECIPIENT[],
  eligibleUsers: ELIGIBLE_USER_FOR_SMS[],
  roleToDepts: Record<string, string[]>
): string[] {
  const numbers = new Set<string>();
  recipients.forEach((r) => {
    if (r.type === "user") {
      const user = eligibleUsers.find((u) => u.id === r.id);
      if (user?.phoneNumber) numbers.add(user.phoneNumber);
    } else if (r.type === "role") {
      eligibleUsers
        .filter((u) => u.role === r.id && u.phoneNumber)
        .forEach((u) => numbers.add(u.phoneNumber));
    } else if (r.type === "department") {
      eligibleUsers
        .filter((u) => (roleToDepts[u.role] ?? []).includes(r.id) && u.phoneNumber)
        .forEach((u) => numbers.add(u.phoneNumber));
    }
  });
  return Array.from(numbers);
}

const COPILOT_BG = "#FAF5FF";
const COPILOT_BORDER = "#E9D4FF";
const COPILOT_SESSION_DOT = "#00C950";
const USER_BUBBLE = "#E1F2FF";
const ASSISTANT_BUBBLE = "#F1E2FF";

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

function GradientCopilotTitle() {
  const { t } = useTranslation();
  return (
    <Typography
      component="span"
      sx={{
        fontSize: 14,
        fontWeight: 700,
        background:
          "linear-gradient(90deg, rgba(148,65,255,1), rgba(238,28,192,0.8), rgba(148,65,255,1))",
        backgroundSize: "200% auto",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        animation: `${gradientShift} 2.2s linear infinite`,
      }}
    >
      {t("Copilot_Name")}
    </Typography>
  );
}

const dotBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-6px); }
`;

function TypingIndicator() {
  return (
    <Box display="flex" alignItems="flex-end" gap={1} mb={1.75}>
      <Icons.CopilotIconBlue
        width={16}
        height={16}
        style={{ flexShrink: 0, marginBottom: 4 }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          bgcolor: ASSISTANT_BUBBLE,
          borderRadius: "12px 12px 12px 0",
          px: 1.5,
          py: 1.25,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: "#98A1B0",
              animation: `${dotBounce} 1.2s ease infinite`,
              animationDelay: `${i * 0.18}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function OnboardingCard() {
  const { t } = useTranslation();
  const bullets = [
    t("Copilot_Onboarding_Bullet_1"),
    t("Copilot_Onboarding_Bullet_2"),
    t("Copilot_Onboarding_Bullet_3"),
    t("Copilot_Onboarding_Bullet_4"),
  ];

  return (
    <Box
      sx={{
        bgcolor: COPILOT_BG,
        borderRadius: 3,
        p: 1.75,
        border: `1px solid ${COPILOT_BORDER}`,
      }}
    >
      <Box display="flex" flexWrap="nowrap" alignItems="center" mb={1.25} gap={0.5}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: "21px", color: "text.primary" }}>
          {t("Copilot_Onboarding_Im")}{" "}
        </Typography>
        <GradientCopilotTitle />
        <Typography sx={{ fontSize: 14, lineHeight: "21px", color: "text.primary" }}>
          {" "}{t("Copilot_Onboarding_Line_Suffix")}
        </Typography>
      </Box>
      {bullets.map((line) => (
        <Box key={line} display="flex" alignItems="flex-start" mb={0.75}>
          <Typography sx={{ fontSize: 14, color: "text.secondary", mr: 1, lineHeight: "21px" }}>
            •
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: "21px", color: "text.primary" }}>
            {line}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}


const MessageBubble = memo(function MessageBubble({
  item,
  onRecommendApprove,
  onRecommendDismiss,
  onBroadcastSend,
  onBroadcastDismiss,
  onBroadcastRetry,
}: {
  item: COPILOT_MESSAGE;
  onRecommendApprove?: (id: string, editedMessage?: string) => void;
  onRecommendDismiss?: (id: string) => void;
  onBroadcastSend?: (messageId: string, recipients: COPILOT_RECIPIENT[], message: string) => void;
  onBroadcastDismiss?: (messageId: string) => void;
  onBroadcastRetry?: (messageId: string, recipients: COPILOT_RECIPIENT[], message: string) => void;
}) {
  const isUser = item.role === "user";

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems={isUser ? "flex-end" : "flex-start"}
      mb={1.75}
    >
      {isUser ? (
        <Box display="flex" alignItems="flex-end" gap={1} justifyContent="flex-end">
          <Box
            sx={{
              maxWidth: "82%",
              bgcolor: USER_BUBBLE,
              borderRadius: "12px 12px 0 12px",
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 14, lineHeight: "20px", color: "text.primary" }}>
              {item.text}
            </Typography>
          </Box>
          <Icons.UserAvatar
            width={16}
            height={16}
            style={{ flexShrink: 0, marginBottom: 4 }}
          />
        </Box>
      ) : (
        <>
          {item.text && (
            <Box display="flex" alignItems="flex-end" gap={1}>
              <Icons.CopilotIconBlue
                width={16}
                height={16}
                style={{ flexShrink: 0, marginBottom: 4 }}
              />
              <Box
                sx={{
                  maxWidth: "82%",
                  bgcolor: ASSISTANT_BUBBLE,
                  borderRadius: "12px 12px 12px 0",
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <MemoizedMarkdown content={item.text} id={item.id} />
              </Box>
            </Box>
          )}
          {item.tags && item.tags.length > 0 && (
            <Box
              display="flex"
              flexDirection="row"
              gap={0.75}
              mt={0.75}
              pl={3}
              sx={{ overflowX: "auto", width: "100%", "&::-webkit-scrollbar": { display: "none" } }}
            >
              {item.tags.map((tag) => (
                <Box
                  key={tag.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    height: 24,
                    border: `1px solid ${tag.color}`,
                    borderRadius: 5,
                    px: 1,
                    bgcolor: tag.bgColor,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: tag.textColor ?? tag.color,
                      lineHeight: 1,
                    }}
                  >
                    {tag.label}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ flexShrink: 0, width: 14 }} />
            </Box>
          )}
          {item.recommendation && (
            <RecommendCard
              recommendation={item.recommendation}
              onApprove={onRecommendApprove ? (editedMessage) => { onRecommendApprove(item.id, editedMessage); } : undefined}
              onDismiss={() => onRecommendDismiss?.(item.id)}
            />
          )}
          {item.broadcast && (
            <Box display="flex" alignItems="flex-end" gap={1} width="100%">
              <Icons.CopilotIconBlue
                width={16}
                height={16}
                style={{ flexShrink: 0, marginBottom: 4 }}
              />
              <BroadcastCard
                messageId={item.id}
                broadcast={item.broadcast}
                onSend={onBroadcastSend}
                onDismiss={onBroadcastDismiss}
                onRetry={onBroadcastRetry}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
});

export const PANEL_WIDTH = 420;
export const PANEL_HEIGHT = 480;
export const EXPANDED_PANEL_WIDTH = 620;
export const EXPANDED_PANEL_HEIGHT = 680;

interface CopilotPanelProps {
  onDragHandlePointerDown: (e: React.PointerEvent) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const CopilotPanel = ({ onDragHandlePointerDown, isExpanded, onToggleExpand }: CopilotPanelProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { messages, isAwaitingResponse, pendingSuggestions } = useCopilotValue();
  const { mutate: sendChat } = useCopilotChatMutation();
  const { data: broadcastData } = useGetBroadcastRecipientsQuery();
  const { mutate: sendSms, mutateAsync: sendSmsAsync } = useSendBroadcastSmsMutation();

  const scrollRef = useRef<HTMLDivElement>(null);

  // Local draft — never in global RxJS state so typing never re-renders other subscribers.
  const [draftInput, setDraftInput] = useState(getCopilotDraft);
  const draftRef = useRef(getCopilotDraft());

  const clearDraft = useCallback(() => {
    clearCopilotDraft();
    draftRef.current = "";
    setDraftInput("");
  }, []);

  // Auto-scroll to bottom when messages or typing state changes.
  useEffect(() => {
    if (scrollRef.current && (messages.length > 0 || isAwaitingResponse)) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, isAwaitingResponse]);

  const [isConfirmingNewSession, setIsConfirmingNewSession] = useState(false);

  const onRequestNewSession = useCallback(() => {
    setIsConfirmingNewSession(true);
  }, []);

  const onConfirmNewSession = useCallback(() => {
    setIsConfirmingNewSession(false);
    startNewCopilotSession();
    clearDraft();
  }, [clearDraft]);

  const onCancelNewSession = useCallback(() => {
    setIsConfirmingNewSession(false);
  }, []);

  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const isHistoryMenuOpen = Boolean(historyAnchorEl);

  const onHistoryButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setHistoryAnchorEl(e.currentTarget);
  }, []);

  const onHistoryMenuClose = useCallback(() => {
    setHistoryAnchorEl(null);
  }, []);

  const onSendMessage = useCallback(() => {
    setHistoryAnchorEl(null);
    appendCopilotBroadcastMessage({
      recipientOptions: broadcastData?.recipientOptions ?? { users: [], roles: [], departments: [] },
    });
  }, [broadcastData]);

  const onShowHistory = useCallback(() => {
    setHistoryAnchorEl(null);
  }, []);

  const onClearHistory = useCallback(() => {
    setHistoryAnchorEl(null);
    startNewCopilotSession();
    clearDraft();
  }, [clearDraft]);

  const executeRecommendation = useCallback(async (msgId: string, suggestionMessage: string) => {
    const personnelDatabase = broadcastData?.personnelDatabase;
    if (!personnelDatabase) {
      updateCopilotRecommendationStatus(msgId, "error");
      return;
    }
    updateCopilotRecommendationStatus(msgId, "executing");
    try {
      const res = await axios.post(
        process.env.REACT_APP_COPILOT_PROCESS_MESSAGE_URL ?? "",
        { personnel_database: personnelDatabase, user_query: suggestionMessage }
      );

      const recipients: Array<{ phoneNumber?: string }> =
        res.data?.response?.recipients ?? [];
      const receiverNumbers = recipients
        .map((r) => r.phoneNumber)
        .filter((p): p is string => !!p);

      if (!receiverNumbers.length) {
        updateCopilotRecommendationStatus(msgId, "error");
        return;
      }

      const smsMessage: string =
        res.data?.response?.parameters?.message ?? suggestionMessage;

      await sendSmsAsync({ receiverNumbers, message: smsMessage });
      updateCopilotRecommendationStatus(msgId, "success");
    } catch {
      updateCopilotRecommendationStatus(msgId, "error");
    }
  }, [broadcastData, sendSmsAsync]);

  const onRecommendApprove = useCallback((id: string, editedMessage?: string) => {
    const msg = getCopilotValue().messages.find((m) => m.id === id);
    if (msg?.recommendation?.status === "executing") return;
    const trimmed = editedMessage?.trim();
    const suggestionMessage = trimmed ?? msg?.recommendation?.title ?? "";
    executeRecommendation(id, suggestionMessage);
  }, [executeRecommendation]);

  const onRecommendDismiss = useCallback((id: string) => {
    updateCopilotRecommendationStatus(id, "dismissed");
  }, []);

  const processSuggestions = useCallback((suggestions: COPILOT_SUGGESTION[]) => {
    suggestions.forEach((suggestion) => {
      const msg = suggestion.message;
      appendCopilotAssistantMessage("", undefined, {
        title: msg ? msg.charAt(0).toUpperCase() + msg.slice(1) : msg,
        reason: suggestion.type,
        recipientOptions: broadcastData?.recipientOptions ?? { users: [], roles: [], departments: [] },
      });
    });
  }, [broadcastData]);

  useEffect(() => {
    if (!pendingSuggestions?.length) return;
    setPendingSuggestions([]);
    processSuggestions(pendingSuggestions);
  }, [pendingSuggestions, processSuggestions]);

  const onBroadcastSend = useCallback((messageId: string, recipients: COPILOT_RECIPIENT[], message: string) => {
    const eligibleUsers = broadcastData?.eligibleUsers ?? [];
    const roleToDepts = broadcastData?.roleToDepts ?? {};
    const receiverNumbers = resolvePhoneNumbers(recipients, eligibleUsers, roleToDepts);

    if (receiverNumbers.length === 0) {
      updateCopilotBroadcastStatus(messageId, "error");
      return;
    }

    const executingCount = receiverNumbers.length;
    updateCopilotBroadcastStatus(messageId, "executing", { executingCount });

    sendSms(
      { receiverNumbers, message },
      {
        onSuccess: () => {
          const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          updateCopilotBroadcastStatus(messageId, "success", {
            successCount: executingCount,
            successSentAt: time,
            successRecipients: recipients.map((r) => r.label),
          });
        },
        onError: () => {
          updateCopilotBroadcastStatus(messageId, "error");
        },
      }
    );
  }, [broadcastData, sendSms]);

  const onBroadcastRetry = useCallback((messageId: string, recipients: COPILOT_RECIPIENT[], message: string) => {
    onBroadcastSend(messageId, recipients, message);
  }, [onBroadcastSend]);

  const onBroadcastDismiss = useCallback((messageId: string) => {
    updateCopilotBroadcastStatus(messageId, "dismissed");
  }, []);

  const canSend = draftInput.trim().length > 0 && !isAwaitingResponse;

  const onSend = useCallback(() => {
    const text = draftRef.current.trim();
    if (!text) return;
    clearDraft();
    if (/^send(\s+a)?\s+message$/i.test(text)) {
      appendCopilotUserMessage(text);
      onSendMessage();
      return;
    }
    appendCopilotUserMessage(text);
    sendChat(text);
  }, [sendChat, clearDraft, onSendMessage]);

  const onChangeDraft = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      setCopilotDraft(text);
      draftRef.current = text;
      setDraftInput(text);
    },
    []
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend]
  );

  return (
    <Box
      sx={{
        width: isExpanded ? EXPANDED_PANEL_WIDTH : PANEL_WIDTH,
        height: isExpanded ? EXPANDED_PANEL_HEIGHT : PANEL_HEIGHT,
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        bgcolor: COPILOT_BG,
        border: `1px solid ${COPILOT_BORDER}`,
        boxShadow: "0 6px 24px rgba(0,0,0,0.14)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 53,
          px: 1.25,
          borderBottom: `1px solid ${COPILOT_BORDER}`,
          bgcolor: COPILOT_BG,
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={0.75}
          flex={1}
          minWidth={0}
          onPointerDown={onDragHandlePointerDown}
          sx={{ cursor: "grab", userSelect: "none", "&:active": { cursor: "grabbing" } }}
        >
          <Icons.CopilotIconBlue width={20} height={20} style={{ flexShrink: 0 }} />
          <Typography
            noWrap
            sx={{ fontSize: 16, fontWeight: 500, color: "text.primary", flexShrink: 1 }}
          >
            {t("Copilot_Name")}
          </Typography>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: COPILOT_SESSION_DOT,
              flexShrink: 0,
            }}
          />
        </Box>
        {isConfirmingNewSession ? (
          <Box display="flex" alignItems="center" gap="10px" flexShrink={0}>
            <Box
              component="button"
              onClick={onConfirmNewSession}
              sx={{
                px: 1.5,
                py: 0.625,
                borderRadius: "4px",
                bgcolor: "#FFE2E2",
                color: "#E00000",
                border: "none",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:hover": { opacity: 0.9 },
                "&:active": { opacity: 0.8 },
              }}
            >
              {t("Copilot_Confirm")}
            </Box>
            <Box
              component="button"
              onClick={onCancelNewSession}
              sx={{
                px: 1.5,
                py: 0.625,
                borderRadius: "4px",
                bgcolor: "#F3F4F6",
                color: "#364153",
                border: "none",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:hover": { opacity: 0.9 },
                "&:active": { opacity: 0.8 },
              }}
            >
              {t("Copilot_Cancel")}
            </Box>
          </Box>
        ) : (
          <>
            <Box display="flex" alignItems="center" gap="10px">
              <Tooltip title={t(isExpanded ? "Copilot_A11y_Collapse" : "Copilot_A11y_Expand")}>
                <IconButton
                  size="small"
                  onClick={onToggleExpand}
                  aria-label={t(isExpanded ? "Copilot_A11y_Collapse" : "Copilot_A11y_Expand")}
                >
                  {isExpanded
                    ? <Icons.Minimise width={16} height={16} />
                    : <Icons.Expand width={16} height={16} />}
                </IconButton>
              </Tooltip>
              <Tooltip title={t("Copilot_A11y_New_Session")}>
                <IconButton
                  size="small"
                  onClick={onRequestNewSession}
                  aria-label={t("Copilot_A11y_New_Session")}
                >
                  <Icons.NewSession width={16} height={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("Copilot_A11y_History")}>
                <IconButton
                  size="small"
                  onClick={onHistoryButtonClick}
                  aria-label={t("Copilot_A11y_History")}
                >
                  <Icons.History width={16} height={16} />
                </IconButton>
              </Tooltip>
            </Box>
            <Menu
              anchorEl={historyAnchorEl}
              open={isHistoryMenuOpen}
              onClose={onHistoryMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    minWidth: 160,
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  },
                },
              }}
            >
              <MenuItem onClick={onSendMessage} sx={{ fontSize: 14, py: 1 }}>
                {t("Copilot_History_Send_Message")}
              </MenuItem>
              <MenuItem onClick={onShowHistory} sx={{ fontSize: 14, py: 1 }}>
                {t("Copilot_History_Show")}
              </MenuItem>
              <MenuItem onClick={onClearHistory} sx={{ fontSize: 14, py: 1, color: "#E00000" }}>
                {t("Copilot_History_Clear")}
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      <Box sx={{ flex: 1, position: "relative", minHeight: 0, overflow: "hidden" }}>
        <Box
          component="img"
          src={CopilotBg}
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <Box
          ref={scrollRef}
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            px: 1.75,
            pt: 1.75,
            pb: 1.75,
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: COPILOT_BORDER,
              borderRadius: 2,
            },
          }}
        >
          {messages.length === 0 && !isAwaitingResponse && <OnboardingCard />}
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              item={m}
              onRecommendApprove={onRecommendApprove}
              onRecommendDismiss={onRecommendDismiss}
              onBroadcastSend={onBroadcastSend}
              onBroadcastDismiss={onBroadcastDismiss}
              onBroadcastRetry={onBroadcastRetry}
            />
          ))}
          {isAwaitingResponse && <TypingIndicator />}
          <Box sx={{ flexShrink: 0, height: 12 }} />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 1.75,
          bgcolor: COPILOT_BG,
          borderTop: `1px solid ${COPILOT_BORDER}`,
          flexShrink: 0,
        }}
      >
        <TextField
          size="small"
          fullWidth
          value={draftInput}
          onChange={onChangeDraft}
          onKeyDown={onKeyDown}
          placeholder={t("Copilot_Input_Placeholder")}
          slotProps={{ htmlInput: { maxLength: 2000 } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: COPILOT_BG,
              fontSize: 14,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(209,213,220,1)",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor:
                theme.palette.custom?.background?.brand ?? "primary.main",
            },
          }}
        />
        <Box
          component="button"
          onClick={canSend ? onSend : undefined}
          aria-label={t("Copilot_A11y_Send")}
          disabled={!canSend}
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "none",
            border: "none",
            padding: 0,
            cursor: canSend ? "pointer" : "default",
            "&:active": canSend ? { opacity: 0.85 } : {},
          }}
        >
          {canSend ? (
            <Icons.SendEnabled width={38} height={38} />
          ) : (
            <Icons.SendDisabled width={38} height={38} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default memo(CopilotPanel);
